import type { Ref } from 'vue';
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import type { EChartsType } from 'echarts/core';

import { ECHARTS_GRID_OPTIONS, ECHARTS_LEGEND_OPTIONS } from '../constants';
import { getCategoryAxisLabelReserve } from '../utils/getCategoryAxisLabelReserve';
import { getEChartsLegendHeight } from '../utils/getEChartsLegendHeight';
import { resolveEChartsInstance } from '../utils/resolveEChartsInstance';

const DEFAULT_LEGEND_LABEL_GAP = 12;
const UPDATE_RETRY_FRAMES = 4;

const getLegendBottomOffset = (): number => {
  const legendOptions = ECHARTS_LEGEND_OPTIONS;
  if (Array.isArray(legendOptions)) return 20;

  const bottom = legendOptions?.bottom;
  return typeof bottom === 'number' ? bottom : 20;
};

type TVueEChartsComponent = Parameters<typeof resolveEChartsInstance>[0];

type TAxisLabelReserve = number | (() => number);

type TUseAdaptiveGridBottomParams = {
  chartRef: Ref<TVueEChartsComponent | null>;
  legendBottom?: number;
  legendLabelGap?: number;
  getCategoryLabels?: () => string[];
  axisLabelReserve?: TAxisLabelReserve;
  fallbackBottom?: number | string;
};

const resolveAxisLabelReserve = ({
  axisLabelReserve,
  getCategoryLabels,
}: Pick<TUseAdaptiveGridBottomParams, 'axisLabelReserve' | 'getCategoryLabels'>): number => {
  if (axisLabelReserve !== undefined) {
    return typeof axisLabelReserve === 'function' ? axisLabelReserve() : axisLabelReserve;
  }

  if (getCategoryLabels) {
    return getCategoryAxisLabelReserve(getCategoryLabels());
  }

  return 0;
};

export const useAdaptiveGridBottom = ({
  chartRef,
  legendBottom = getLegendBottomOffset(),
  legendLabelGap = DEFAULT_LEGEND_LABEL_GAP,
  getCategoryLabels,
  axisLabelReserve,
  fallbackBottom = ECHARTS_GRID_OPTIONS.bottom ?? '14%',
}: TUseAdaptiveGridBottomParams) => {
  const gridBottom = ref<number | string>(fallbackBottom);
  let boundChart: EChartsType | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let chartRenderedHandler: (() => void) | null = null;
  let lastMeasuredLegendHeight = -1;
  let pendingRetryFrame = 0;

  const applyGridBottom = (chart: EChartsType, nextBottom: number | string): void => {
    chart.setOption({ grid: { bottom: nextBottom } });
  };

  const getNextGridBottom = (legendHeight: number): number | string => {
    if (legendHeight <= 0) return fallbackBottom;

    return (
      legendBottom + legendHeight + legendLabelGap + resolveAxisLabelReserve({ axisLabelReserve, getCategoryLabels })
    );
  };

  const updateGridBottom = (): void => {
    const chart = resolveEChartsInstance(chartRef.value);
    if (!chart || chart.isDisposed?.()) return;

    const legendHeight = getEChartsLegendHeight(chart);
    const nextBottom = getNextGridBottom(legendHeight);

    if (legendHeight === lastMeasuredLegendHeight && gridBottom.value === nextBottom) return;

    lastMeasuredLegendHeight = legendHeight;
    if (gridBottom.value === nextBottom) return;

    gridBottom.value = nextBottom;
    applyGridBottom(chart, nextBottom);
  };

  const scheduleUpdate = (retryFrames = 0): void => {
    cancelAnimationFrame(pendingRetryFrame);

    let framesLeft = retryFrames;

    const tick = (): void => {
      updateGridBottom();

      if (framesLeft > 0) {
        framesLeft -= 1;
        pendingRetryFrame = requestAnimationFrame(tick);
      }
    };

    pendingRetryFrame = requestAnimationFrame(tick);
  };

  const unbindChart = (): void => {
    if (boundChart && chartRenderedHandler) {
      boundChart.off('rendered', chartRenderedHandler);
    }
    boundChart = null;
    chartRenderedHandler = null;
    resizeObserver?.disconnect();
    resizeObserver = null;
    lastMeasuredLegendHeight = -1;
    cancelAnimationFrame(pendingRetryFrame);
  };

  const bindChart = (component: TVueEChartsComponent | null): void => {
    unbindChart();

    const chart = resolveEChartsInstance(component);
    if (!chart) return;

    boundChart = chart;
    chartRenderedHandler = () => {
      scheduleUpdate();
    };
    chart.on('rendered', chartRenderedHandler);

    const dom = chart.getDom?.();
    if (dom && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        scheduleUpdate();
      });
      resizeObserver.observe(dom);
    }

    scheduleUpdate(UPDATE_RETRY_FRAMES);
  };

  watch(
    chartRef,
    (component) => {
      nextTick(() => {
        bindChart(component);
      });
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    unbindChart();
  });

  return { gridBottom };
};
